"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useState } from "react";
import { Search, User } from "lucide-react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
function CategoryShortcuts() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-4 overflow-x-auto py-2">
        {[1, 2, 3, 4, 5].map((_, i) => <div key={i} data-index={i} className="h-5 w-20 animate-pulse rounded-md bg-muted/50" />)}
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    const fallbackCategories = ["Smart Cameras", "Smart Locks", "Alarm Systems", "Video Doorbells", "Home Security", "Smoke Detectors"];
    return <nav className="flex gap-6 overflow-x-auto py-2 scrollbar-hide">
        {fallbackCategories.map((category, i) => <Link key={i} to="#" data-index={i} className="whitespace-nowrap text-[14px] font-default font-semibold text-foreground transition-colors hover:text-secondary">
            {category}
          </Link>)}
      </nav>;
  }
  return <nav className="flex gap-6 overflow-x-auto py-2 scrollbar-hide">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} data-index={i} className="whitespace-nowrap text-[14px] font-default font-semibold text-foreground transition-colors hover:text-secondary">
          {item.label}
        </Link>)}
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="264780" id="header" className={`sticky top-0 z-50 w-full border-b transition-all duration-300 ${isScrolled ? "border-border bg-background/80 backdrop-blur-md shadow-sm" : "border-transparent bg-background"} text-foreground`}>
      <div className="container mx-auto px-4 pt-4 pb-2">
        {}
        <div className="flex items-center justify-between gap-4 md:gap-8">
          {}
          <div className="flex-shrink-0">
            <Link to="/" className="block">
              <WvcLogo className="h-8 w-auto text-primary" />
            </Link>
          </div>

          <div className="hidden lg:flex items-center gap-6">
            <div className="flex flex-col">
              
              <div className="flex gap-4">
                <Link to="#" className="whitespace-nowrap text-[14px] font-default font-semibold text-foreground transition-colors hover:text-secondary"><br /></Link>
                
                
              </div>
            </div>
          </div>

          {}
          <div className="hidden flex-1 max-w-3xl md:block">
            <div className="flex items-center rounded-lg border border-border bg-card p-1 focus-within:ring-2 focus-within:ring-primary/20">
              <div className="flex-1 px-2">
                <Input type="search" placeholder="Search for security systems, cameras and guides" className="h-8 border-0 bg-transparent px-0 font-default text-sm focus-visible:ring-0 focus-visible:ring-offset-0" />
              </div>
              <Button size="sm" className="h-8 rounded-md px-6 bg-secondary text-secondary-foreground hover:bg-secondary/90 font-default font-semibold">
                <Search className="mr-2 size-4" />
                Find Your Best Match
              </Button>
            </div>
          </div>

          {}
          <div className="flex items-center gap-4 flex-shrink-0">
            <SafeHydrate><Avatar className="size-8 cursor-pointer border border-border shadow-sm">
              <AvatarImage src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687986/img/user_avatar.png" alt="User" />
              <AvatarFallback className="bg-muted text-muted-foreground">
                <User className="size-4" />
              </AvatarFallback>
            </Avatar></SafeHydrate>
          </div>
        </div>

        {}
        <div className="mt-3 block md:hidden">
          <div className="flex items-center rounded-lg border border-border bg-card p-1 focus-within:ring-2 focus-within:ring-primary/20">
            <Input type="search" placeholder="Search products..." className="h-8 flex-1 border-0 bg-transparent px-2 font-default text-sm focus-visible:ring-0 focus-visible:ring-offset-0" />
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 rounded-md text-foreground">
              <Search className="size-4 text-muted-foreground" />
            </Button>
          </div>
        </div>

        {}
        <div className="mt-2 hidden md:block">
          <MenuProvider menu_id="38">
            <CategoryShortcuts />
          </MenuProvider>
        </div>
      </div>
    </section>;
}